// JogIdsToRetrieveAllWspEvents is based on the SetJogIds(DispatchEvent data) function,
// in vcc-app-dispatch, where JogId is set for WSP dispatch events:
// https://dev.azure.com/WSDOT/VCC/_git/vcc-app-dispatch?path=/src/functions/DispatchEventPrepare/Function.cs&version=GBjog-main&line=160&lineEnd=161&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents

import { AgencyAbbr, AreaCode, RegionCode, FilterKey, Agency, AgencyIds } from ".";

// Labels that can be edited
enum WspDistrictLabel {
  D1_TAC = "TAC", // Tacoma
  D2_SEA = "SEA", // Seattle
  D3_YAK = "YAK", // Yakima
  D4_SPO = "SPO", // Spokane
  D5_VAN = "VAN", // Vancouver
  D6_WEN = "WEN", // Wenatchee
  D7_NW = "NW",   // Northwest
  D8_PEN = "PEN", // Kitsap Peninsula
}

export const AgencyDelimeter = ": ";

// IDs that should not be edited, because they're saved in UserPreferences
// If they are changed, the conversion in the saga for loading preferences needs to be updated.
export class WspDistrictFilterKey  {
  static TAC_D1 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D1
  static SEA_D2 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D2
  static YAK_D3 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D3
  static SPO_D4 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D4
  static VAN_D5 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D5
  static WEN_D6 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D6
  static NW_D7 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D7
  static PEN_D8 = AgencyIds.WSP + AgencyDelimeter + AreaCode.WSP_D8
}

// Labels that can be edited
export class WspDistrcitFilterLabel {
  // todo: jcavner: Consider whether we still need these as AgencyAbbrs or not.
  // May just need the AgencyRegionCode values. UI could use the combo of both enums, merhaps.
  // E.g., AgencyAbbr.WSP + ":" + AgencyRegionCode.NW
  static WSP = AgencyAbbr.WSP;
  static TAC_D1 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D1_TAC;  // District 1 (Pierce and Thurston county)
  static SEA_D2 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D2_SEA;  // District 2 (King County)
  static YAK_D3 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D3_YAK;  // District 3
  static SPO_D4 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D4_SPO;  // District 4
  static VAN_D5 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D5_VAN;  // District 5
  static WEN_D6 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D6_WEN;  // District 6
  static NW_D7 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D7_NW;    // District 7
  static PEN_D8 = AgencyAbbr.WSP + AgencyDelimeter + WspDistrictLabel.D8_PEN;  // District 8

  // Unused
  static I5 = AgencyAbbr.WSP + AgencyDelimeter + AreaCode.I5; // Subset of District 2

  // Obsolete
  static S_D3_obsolete = AgencyAbbr.WSP + AgencyDelimeter + RegionCode.S;   // District 3
  static E_D4_obsolete = AgencyAbbr.WSP + AgencyDelimeter + RegionCode.E;   // District 4
  static SW_D5_obsolete = AgencyAbbr.WSP + AgencyDelimeter + RegionCode.SW; // District 5
  static NC_D6_obsolete = AgencyAbbr.WSP + AgencyDelimeter + RegionCode.NC; // District 6
}

// Purposely exporting this map for readability. Where it's used, you can
// tell if there's not a converted value the existing value should be used.
// todo: jcavner: rename to WspDistrictFilterKeyConversionMap ? I left in generic so I could add other conversions if I needed/wanted to. But I could always create a differet map for that, so each is specific.
export const AgencyFilterConversionMap = new Map<FilterKey, string>([  
  // Obsolete WSP filters
  [WspDistrcitFilterLabel.TAC_D1, WspDistrictFilterKey.TAC_D1],
  [WspDistrcitFilterLabel.SEA_D2, WspDistrictFilterKey.SEA_D2],
  [WspDistrcitFilterLabel.S_D3_obsolete, WspDistrictFilterKey.YAK_D3],
  [WspDistrcitFilterLabel.E_D4_obsolete, WspDistrictFilterKey.SPO_D4],
  [WspDistrcitFilterLabel.SW_D5_obsolete, WspDistrictFilterKey.VAN_D5],
  [WspDistrcitFilterLabel.NC_D6_obsolete, WspDistrictFilterKey.WEN_D6],
  [WspDistrcitFilterLabel.NW_D7, WspDistrictFilterKey.NW_D7],
  [WspDistrcitFilterLabel.PEN_D8, WspDistrictFilterKey.PEN_D8],
]);

// Purposely not exporting the map
const AgencyFilterLabels = new Map<WspDistrictFilterKey, string>([
  [WspDistrictFilterKey.TAC_D1, WspDistrcitFilterLabel.TAC_D1],
  [WspDistrictFilterKey.SEA_D2, WspDistrcitFilterLabel.SEA_D2],
  [WspDistrictFilterKey.YAK_D3, WspDistrcitFilterLabel.YAK_D3],
  [WspDistrictFilterKey.SPO_D4, WspDistrcitFilterLabel.SPO_D4],
  [WspDistrictFilterKey.VAN_D5, WspDistrcitFilterLabel.VAN_D5],
  [WspDistrictFilterKey.WEN_D6, WspDistrcitFilterLabel.WEN_D6],
  [WspDistrictFilterKey.NW_D7, WspDistrcitFilterLabel.NW_D7],
  [WspDistrictFilterKey.PEN_D8, WspDistrcitFilterLabel.PEN_D8],
]);

// Using this function instead of direct map access to uniformly return the "agencyId" if there is no label.
// This works currently because all of the filters that aren't in the map use their AreaCode as the ID.
export const GetAgencyFilterLabel = (agency: Agency): string => {
  let label = AgencyFilterLabels.get(agency.id) // Retrieve labels like "WSP: TAC" for keys like "WSP: D1"
    || agency.abbreviation
    || agency.id;
  
  return label;
}

// Used in agency-chip
export const getWspLabel = (area: AreaCode | string = ""): WspDistrcitFilterLabel => {
  switch (area) {
    case AreaCode.WSP_D1: return WspDistrcitFilterLabel.TAC_D1;
    case AreaCode.WSP_D2:
    case AreaCode.I5: // The area value of D2 events within specific coordinates
      return WspDistrcitFilterLabel.SEA_D2;
    case AreaCode.WSP_D3: return WspDistrcitFilterLabel.YAK_D3;
    case AreaCode.WSP_D4: return WspDistrcitFilterLabel.SPO_D4;
    case AreaCode.WSP_D5: return WspDistrcitFilterLabel.VAN_D5;
    case AreaCode.WSP_D6: return WspDistrcitFilterLabel.WEN_D6;
    case AreaCode.WSP_D7: return WspDistrcitFilterLabel.NW_D7;
    case AreaCode.WSP_D8: return WspDistrcitFilterLabel.PEN_D8;
    default:
      return WspDistrcitFilterLabel.WSP;
  }
};

// Used in filter click handler
export const getWspDistrictAgencyId = (area: AreaCode | string = ""): WspDistrictFilterKey | undefined => {
  switch (area) {
    case AreaCode.WSP_D1: return WspDistrictFilterKey.TAC_D1;
    case AreaCode.WSP_D2:
    case AreaCode.I5: // The area value of D2 events within specific coordinates
      return WspDistrictFilterKey.SEA_D2;
    case AreaCode.WSP_D3: return WspDistrictFilterKey.YAK_D3;
    case AreaCode.WSP_D4: return WspDistrictFilterKey.SPO_D4;
    case AreaCode.WSP_D5: return WspDistrictFilterKey.VAN_D5;
    case AreaCode.WSP_D6: return WspDistrictFilterKey.WEN_D6;
    case AreaCode.WSP_D7: return WspDistrictFilterKey.NW_D7;
    case AreaCode.WSP_D8: return WspDistrictFilterKey.PEN_D8;
    default: return undefined;
  }
};